import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	ApplicationSummaryList,
	ApplicationsFilter,
	Batch,
	PagingConfig,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useAuth } from '../../../hooks/useAuth';
import { getApplications } from '../../../service/VolunteerApplicationService';
import { useBatchId } from '../../../hooks/useBatchId';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
	ApplicationStatus,
	DEFAULT_PAGE_SIZE,
} from '../../../shared/Constants';
import { useSnapshot } from 'valtio';
import { ApplicationsListState } from './ApplicationsList.store';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { BatchInfoState } from '../../../components/BatchInfo.store';
import { getAdminCentralTeamFilters } from '../../../shared/Utils';
import { CentralTeamDashboardFilter } from '../../../shared/ScreeningServiceTypes';
import useUserSession from '../../../hooks/useUserSession';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';

let lastCompletedFetchKey: string | null = null;
let inflightFetchKey: string | null = null;

const EMPTY_APPLICATIONS_LIST: ApplicationSummaryList = {
	applicationSummary: [],
	pagination: {
		pageNo: 1,
		pageSize: DEFAULT_PAGE_SIZE,
		totalNoOfPages: 0,
		totalNoOfRecords: 0,
	},
};

export function resetApplicationsListCache() {
	lastCompletedFetchKey = null;
	inflightFetchKey = null;
	ApplicationsListState.applicationsList = EMPTY_APPLICATIONS_LIST;
}

export const useApplicationsList = ({
	categoryKey,
	CTDFilter,
	fromCTD = false,
}: {
	categoryKey?: string | null;
	CTDFilter?: CentralTeamDashboardFilter;
	fromCTD?: boolean;
}) => {
	const { initiated, firebaseId, srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { batchId } = useBatchId();
	const { i18n } = useTranslation();
	const filterStateSnap = useSnapshot(ApplicationsListState);
	const batchStateSnap = useSnapshot(BatchInfoState);
	const batchInfo = batchStateSnap.batchInfo;
	const { setLoading } = useLoadingIndicator();

	const [paging, setPaging] = useState<PagingConfig>(() => {
		return { ...filterStateSnap.paging } as PagingConfig;
	});
	const [filter, setFilter] = useState<ApplicationsFilter>();
	const [selectedBatch, setSelectedBatch] = useState<Batch>();

	const currentFetchKey = useMemo(() => {
		if (!filter || !initiated || !firebaseId || !srcmId || !selectedRole) {
			return null;
		}

		const selectedBatchDetails = sessionStorage.getItem('SELECTED_BATCH');
		let resolvedBatchId = batchId;
		if (selectedBatchDetails) {
			resolvedBatchId = (JSON.parse(selectedBatchDetails) as Batch)
				.id as number;
		}

		const pageNumber = paging.pageNo ?? 1;
		const pageSize = paging.pageSize ?? DEFAULT_PAGE_SIZE;
		const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;

		return JSON.stringify({
			resolvedBatchId,
			firebaseId,
			filter,
			pageIndex,
			pageSize,
			srcmId,
			language: i18n.language,
			selectedRole,
		});
	}, [
		filter,
		initiated,
		firebaseId,
		srcmId,
		selectedRole,
		batchId,
		paging,
		i18n.language,
	]);

	const { showToastMessage } = useToastMessage();

	const applicationsList =
		filterStateSnap.applicationsList as ApplicationSummaryList;

	useEffect(() => {
		if (filterStateSnap.filterBy && !categoryKey) {
			setFilter({ ...filterStateSnap.filterBy } as ApplicationsFilter);
		}
	}, [categoryKey, filterStateSnap.filterBy]);

	useEffect(() => {
		if ((filterStateSnap.filterBy || !batchInfo) && !categoryKey) {
			return;
		}

		const isValidDate = (dateStr?: string) =>
			dateStr &&
			dateStr !== '1970-01-01T00:00:00Z' &&
			moment().isSameOrAfter(dateStr);

		let defaultFilters: ApplicationsFilter | undefined;
		if (fromCTD) {
			defaultFilters = getAdminCentralTeamFilters(categoryKey, CTDFilter);
		} else if (selectedRole === UserRole.CARE_TEAM) {
			defaultFilters = {
				status: [
					ApplicationStatus.FUNCTIONARY_ASSIGNED,
					ApplicationStatus.FUNCTIONARY_SUBMITTED,
					ApplicationStatus.WAITING_FOR_CARE_TEAM,
					ApplicationStatus.APPROVED,
				],
			};
		} else if (
			selectedRole === UserRole.ZC &&
			isValidDate(batchInfo?.snpResultDateForZC)
		) {
			defaultFilters = { status: [ApplicationStatus.APPROVED] };
		} else if (
			selectedRole === UserRole.APPROVER &&
			isValidDate(batchInfo?.snpResultDateForApprover)
		) {
			defaultFilters = { status: [ApplicationStatus.APPROVED] };
		} else if (selectedRole === UserRole.ZC) {
			defaultFilters = {
				status: [
					ApplicationStatus.FUNCTIONARY_ASSIGNED,
					ApplicationStatus.FUNCTIONARY_SUBMITTED,
				],
			};
		} else if (selectedRole === UserRole.APPROVER) {
			defaultFilters = { status: [ApplicationStatus.FUNCTIONARY_SUBMITTED] };
		} else if (
			selectedRole === UserRole.SNP_ADMIN &&
			selectedBatch?.status === 'INACTIVE'
		) {
			defaultFilters = { status: [ApplicationStatus.APPROVED] };
		} else if (
			selectedRole === UserRole.SNP_ADMIN ||
			selectedRole === UserRole.EXAM_ADMIN
		) {
			defaultFilters = getAdminCentralTeamFilters(categoryKey, CTDFilter);
		}

		if (!defaultFilters) {
			defaultFilters = { status: [ApplicationStatus.FUNCTIONARY_ASSIGNED] };
		}

		setFilter(defaultFilters);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRole, batchInfo, categoryKey, CTDFilter, fromCTD]);

	const onPageChange = useCallback((pageNumber: number, pageSize: number) => {
		ApplicationsListState.paging = { pageNo: pageNumber, pageSize };
		setPaging({ pageNo: pageNumber, pageSize });
	}, []);

	const onFilterChange = useCallback(
		(filterParam: ApplicationsFilter) => {
			ApplicationsListState.filterBy = { ...filterParam };
			setFilter(filterParam);
			onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onPageChange, paging.pageSize]
	);

	const resetFilters = useCallback(() => {
		ApplicationsListState.filterBy = undefined;
		setFilter(undefined);
		onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
	}, [onPageChange, paging.pageSize]);

	useEffect(() => {
		if (!currentFetchKey || currentFetchKey === inflightFetchKey) {
			return;
		}
		if (currentFetchKey === lastCompletedFetchKey) {
			return;
		}
		if (!filter || !initiated || !firebaseId || !srcmId || !selectedRole) {
			return;
		}

		inflightFetchKey = currentFetchKey;
		setLoading(true);
		const selectedBatchDetails = sessionStorage.getItem('SELECTED_BATCH');
		let resolvedBatchId = batchId;
		if (selectedBatchDetails) {
			const parsedBatch = JSON.parse(selectedBatchDetails) as Batch;
			setSelectedBatch(parsedBatch);
			resolvedBatchId = parsedBatch.id as number;
		}

		const pageNumber = paging.pageNo ?? 1;
		const pageSize = paging.pageSize ?? DEFAULT_PAGE_SIZE;
		const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;

		void (async () => {
			try {
				const applications = await getApplications({
					batchId: resolvedBatchId,
					firebaseId,
					filter,
					pageIndex,
					pageSize,
					functionarySrcmId: srcmId,
					predefinedReasonLanguage: i18n.language,
					selectedRoleCode: selectedRole,
				});

				lastCompletedFetchKey = currentFetchKey;

				if (
					applications.pagination.totalNoOfPages &&
					applications.pagination.totalNoOfPages > 0 &&
					pageNumber > applications.pagination.totalNoOfPages
				) {
					onPageChange(applications.pagination.totalNoOfPages ?? 1, pageSize);
				} else {
					ApplicationsListState.applicationsList = applications;
				}
			} catch (e) {
				if (e instanceof Error) {
					showToastMessage('error', e.message);
				} else {
					showToastMessage('error', 'Unknown error occurred');
				}
			} finally {
				inflightFetchKey = null;
				setLoading(false);
			}
		})();
	}, [
		paging,
		filter,
		batchId,
		initiated,
		firebaseId,
		srcmId,
		selectedRole,
		i18n.language,
		onPageChange,
		showToastMessage,
		currentFetchKey,
		setLoading,
	]);

	return {
		applicationsList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		selectedBatch,
		resetFilters,
	};
};
